import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { PageTransition } from "./PageTransition";
import { ErrorBoundary } from "./ErrorBoundary";
import { ProtectedRoute } from "./ProtectedRoute";
import { AdminRoute } from "./AdminRoute";
import { AgentAccessGuard } from "./access/AgentAccessGuard";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import { AGENTS } from "@/config/brand";

// Public marketing pages stay eager — they share the same Header/Footer shell and
// users navigate between them frequently. Lazy-loading them caused a visible
// white flash (Suspense fallback) before the dark hero gradient painted.
import Index from "@/pages/Index";
import Solutions from "@/pages/Solutions";
import Eqms from "@/pages/Eqms";
import RegulatoryHubMarketing from "@/pages/RegulatoryHubMarketing";
import Platform from "@/pages/Platform";
import AIGovernance from "@/pages/AIGovernance";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import SecurityCompliance from "@/pages/SecurityCompliance";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import NotFound from "@/pages/NotFound";

// Auth pages are lazy — they pull in @supabase/auth-js, react-hook-form, and
// zod (the largest "unused JS" chunks on the landing page per Lighthouse).
// They render their own dark layout, so the navy fallback is invisible.
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const SsoLaunch = lazy(() => import("@/pages/SsoLaunch"));

// Lower-traffic / utility routes can stay lazy
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Subscriptions = lazy(() => import("@/pages/Subscriptions"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Status = lazy(() => import("@/pages/Status"));
const Changelog = lazy(() => import("@/pages/Changelog"));

const agentByRoute = Object.fromEntries(AGENTS.map((agent) => [agent.route, agent]));

const AgentsHub = lazy(() => import("@/pages/dashboard/AgentsHub"));
const Investigations = lazy(() => import("@/pages/dashboard/Investigations"));
const Inspections = lazy(() => import("@/pages/dashboard/Inspections"));
const Contracts = lazy(() => import("@/pages/dashboard/Contracts"));
const Complaints = lazy(() => import("@/pages/dashboard/Complaints"));
const LabInvestigations = lazy(() => import("@/pages/dashboard/LabInvestigations"));
const SupplierQualifications = lazy(() => import("@/pages/dashboard/SupplierQualifications"));
const QMS = lazy(() => import("@/pages/dashboard/QMS"));
const Operations = lazy(() => import("@/pages/dashboard/Operations"));
const KnowledgeBase = lazy(() => import("@/pages/dashboard/KnowledgeBase"));
const RegulatoryHub = lazy(() => import("@/pages/dashboard/RegulatoryHub"));
const AgentManager = lazy(() => import("@/pages/admin/AgentManager"));
const SubscriberList = lazy(() => import("@/pages/admin/SubscriberList"));
const ReportTemplates = lazy(() => import("@/pages/admin/ReportTemplates"));
const ExpertReviews = lazy(() => import("@/pages/admin/ExpertReviews"));
const AuditTrail = lazy(() => import("@/pages/admin/AuditTrail"));
const AdminTools = lazy(() => import("@/pages/admin/AdminTools"));
const OrgQMS = lazy(() => import("@/pages/admin/OrgQMS"));
const PlaybooksManager = lazy(() => import("@/pages/admin/PlaybooksManager"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const SubscriptionMonitoring = lazy(() => import("@/pages/admin/SubscriptionMonitoring"));
const ReviewerDashboard = lazy(() => import("@/pages/reviewer/ReviewerDashboard"));
const ReviewInvestigation = lazy(() => import("@/pages/reviewer/ReviewInvestigation"));
const WorkspaceAgents = lazy(() => import("@/pages/workspace/WorkspaceAgents"));
const WorkspaceLibrary = lazy(() => import("@/pages/workspace/WorkspaceLibrary"));
const WorkspaceHistory = lazy(() => import("@/pages/workspace/WorkspaceHistory"));
const WorkspaceQMS = lazy(() => import("@/pages/workspace/WorkspaceQMS"));
const WorkspaceNotifications = lazy(() => import("@/pages/workspace/WorkspaceNotifications"));
const SeoLanding = lazy(() => import("@/pages/seo/SeoLanding"));
const EmailTemplates = lazy(() => import("@/pages/admin/EmailTemplates"));
const EmailMonitoring = lazy(() => import("@/pages/admin/EmailMonitoring"));
const OrganizationsManager = lazy(() => import("@/pages/admin/OrganizationsManager"));
const AgentAccessManager = lazy(() => import("@/pages/admin/AgentAccessManager"));
const IntegrationHealth = lazy(() => import("@/pages/admin/IntegrationHealth"));



// Routes that show the shared Header + Footer (rendered outside animation)
const headerFooterRoutes = [
  "/", "/solutions", "/ai-agents", "/spine", "/eqms", "/regulatory-hub",
  "/platform", "/about", "/contact",
  "/licensing", "/subscriptions", "/payment-success", "/auth", "/security-compliance", "/terms", "/privacy", "/unsubscribe", "/reset-password",
  "/how-it-works", "/status", "/changelog", "/dashboard/regulatory-hub", "/dashboard/agents",
  "/admin/tools", "/admin/subscribers", "/admin/expert-reviews", "/admin/email-templates", "/admin/email-monitoring", "/admin/organizations", "/admin/agent-access",
  "/admin/playbooks", "/admin/subscription-monitoring", "/admin/integration-health",
  "/reviewer/dashboard",
];

function matchesHeaderRoute(pathname: string) {
  return (
    headerFooterRoutes.includes(pathname) ||
    pathname.startsWith("/reviewer/investigation/") ||
    pathname.startsWith("/solutions/")
  );
}

// Fallback uses the deep navy hero color so lazy chunk fetches don't flash
// a white band before the dark hero gradient paints.
const RouteFallback = () => (
  <div
    className="min-h-screen"
    style={{ backgroundColor: "hsl(var(--navy-deep))" }}
    aria-hidden="true"
  />
);

export function AnimatedRoutes() {
  const location = useLocation();
  const showHeaderFooter = matchesHeaderRoute(location.pathname);

  return (
    <>
      {/* Header lives OUTSIDE AnimatePresence — never unmounts between routes */}
      {showHeaderFooter && <Header />}
      {showHeaderFooter && <Breadcrumbs />}

      <AnimatePresence mode="wait">
        <PageTransition key={location.pathname}>
          <ErrorBoundary fallbackTitle="Page Error">
            <Suspense fallback={<RouteFallback />}>
              <Routes location={location}>
                {/* Public / full-page routes */}
                <Route path="/" element={<Index />} />
                {/* Public marketing routes match their navigation labels. */}
                <Route path="/ai-agents" element={<AIGovernance />} />
                <Route path="/solutions" element={<Solutions />} />
                {/* Hidden SEO landing pages — discoverable only via sitemap & search */}
                <Route path="/solutions/:slug" element={<SeoLanding />} />
                {/* New pillar marketing pages */}
                <Route path="/spine" element={<Eqms />} />
                <Route path="/eqms" element={<Navigate to="/spine" replace />} />
                <Route path="/regulatory-hub" element={<RegulatoryHubMarketing />} />
                <Route path="/platform" element={<Platform />} />
                <Route path="/how-it-works" element={<Navigate to="/platform" replace />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/resources" element={<Navigate to="/platform" replace />} />
                <Route path="/licensing" element={<Subscriptions />} />
                <Route path="/subscriptions" element={<Navigate to="/licensing" replace />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/sso/launch" element={<SsoLaunch />} />
                <Route path="/security-compliance" element={<SecurityCompliance />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/status" element={<Status />} />
                <Route path="/changelog" element={<Changelog />} />
                {/* Gated regulatory dashboard moved out of the public URL space */}
                <Route path="/dashboard/regulatory-hub" element={<ProtectedRoute><RegulatoryHub /></ProtectedRoute>} />
                <Route path="/dashboard/agents" element={<ProtectedRoute><AgentsHub /></ProtectedRoute>} />
                <Route path="/admin/tools" element={<AdminRoute><AdminTools /></AdminRoute>} />
                <Route path="/admin/subscribers" element={<AdminRoute><SubscriberList /></AdminRoute>} />
                <Route path="/admin/expert-reviews" element={<AdminRoute><ExpertReviews /></AdminRoute>} />
                <Route path="/reviewer/dashboard" element={<ProtectedRoute><ReviewerDashboard /></ProtectedRoute>} />
                <Route path="/reviewer/investigation/:reviewId" element={<ProtectedRoute><ReviewInvestigation /></ProtectedRoute>} />

                {/* Dashboard workspace routes — own DashboardLayout, no shared Header */}
                <Route path="/dashboard/investigations" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/investigations"].accessId} agentLabel={agentByRoute["/dashboard/investigations"].workspaceName}><Investigations /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/inspections" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/inspections"].accessId} agentLabel={agentByRoute["/dashboard/inspections"].workspaceName}><Inspections /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/contracts" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/contracts"].accessId} agentLabel={agentByRoute["/dashboard/contracts"].workspaceName}><Contracts /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/complaints" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/complaints"].accessId} agentLabel={agentByRoute["/dashboard/complaints"].workspaceName}><Complaints /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/lab-investigations" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/lab-investigations"].accessId} agentLabel={agentByRoute["/dashboard/lab-investigations"].workspaceName}><LabInvestigations /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/supplier-qualifications" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/supplier-qualifications"].accessId} agentLabel={agentByRoute["/dashboard/supplier-qualifications"].workspaceName}><SupplierQualifications /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/qms" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/qms"].accessId} agentLabel={agentByRoute["/dashboard/qms"].workspaceName}><QMS /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/operations" element={<ProtectedRoute><AgentAccessGuard agentId={agentByRoute["/dashboard/operations"].accessId} agentLabel={agentByRoute["/dashboard/operations"].workspaceName}><Operations /></AgentAccessGuard></ProtectedRoute>} />
                <Route path="/dashboard/knowledge-base" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
                <Route path="/admin/agents" element={<AdminRoute><AgentManager /></AdminRoute>} />
                <Route path="/admin/report-templates" element={<AdminRoute><ReportTemplates /></AdminRoute>} />
                <Route path="/admin/audit-trail" element={<AdminRoute><AuditTrail /></AdminRoute>} />
                <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                <Route path="/admin/subscription-monitoring" element={<AdminRoute><SubscriptionMonitoring /></AdminRoute>} />
                <Route path="/admin/org-qms" element={<AdminRoute><OrgQMS /></AdminRoute>} />
                <Route path="/admin/playbooks" element={<AdminRoute><PlaybooksManager /></AdminRoute>} />
                <Route path="/admin/email-templates" element={<AdminRoute><EmailTemplates /></AdminRoute>} />
                <Route path="/admin/email-monitoring" element={<AdminRoute><EmailMonitoring /></AdminRoute>} />
                <Route path="/admin/organizations" element={<AdminRoute><OrganizationsManager /></AdminRoute>} />
                <Route path="/admin/agent-access" element={<AdminRoute><AgentAccessManager /></AdminRoute>} />
                <Route path="/admin/integration-health" element={<AdminRoute><IntegrationHealth /></AdminRoute>} />

                {/* Workspace routes — own layout, no shared Header */}
                <Route path="/my-workspace" element={<ProtectedRoute><Navigate to="/my-workspace/agents" replace /></ProtectedRoute>} />
                <Route path="/my-workspace/agents" element={<ProtectedRoute><WorkspaceAgents /></ProtectedRoute>} />
                <Route path="/my-workspace/qms" element={<ProtectedRoute><WorkspaceQMS /></ProtectedRoute>} />
                <Route path="/my-workspace/library" element={<ProtectedRoute><WorkspaceLibrary /></ProtectedRoute>} />
                <Route path="/my-workspace/notifications" element={<ProtectedRoute><WorkspaceNotifications /></ProtectedRoute>} />
                <Route path="/my-workspace/history" element={<ProtectedRoute><WorkspaceHistory /></ProtectedRoute>} />

                {/* Pharmalify eQMS (validated zone) lives in a separate Lovable project
                    bound to its own Supabase. See eqms-handoff/ for the bootstrap bundle. */}




                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </PageTransition>
      </AnimatePresence>

      {/* Footer also outside AnimatePresence */}
      {showHeaderFooter && <Footer />}
    </>
  );
}
